/* eslint-disable max-lines */
// deps
import React, {
    FormEvent,
    memo,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";

import { isEmpty } from "@swiggy-private/js-utils";
import { WidgetControllerContext } from "@swiggy-private/dls-react-mxn";
import { DropdownChip } from "@swiggy-private/dls-web-core/dist/components/Chips/DropwdownChip";

import { HalfCard } from "@swiggy-private/dls-web-core/dist/components/HalfCard";
import { Text } from "@swiggy-private/dls-web-core/dist/components/Text";
import { SpriteIconWeb } from "@swiggy-private/dls-web-icons/dist/SpriteIconWeb";
import { ACTIONS, TEXTS, COMMON_IDS, STICKY_OFFSET } from "./constants";
import HalfCardContent from "./HalfCardContent/HalfCardContent";
import { getBoundingClient } from "./helpers";
import {
    getAllSelectedFilter,
    getDefaultSortValue,
    getSelectedSortValue,
    getSelectedSortLabel,
    cleanFilterData,
    transformSortObject,
} from "./helpers";

import OpenFilterChips from "./OpenFilterChips";
import {
    RefineChip,
    RefineWidgetWrapper,
    StyledButtonChip,
    StyledButtonChipSpan,
    RefineWidgetPlaceholder,
    InnerSpan,
} from "./styles";
import { IFacetInfo, IRefineWidget } from "./types";
import { IFacetList } from "./types";

const RefineWidget = (props: IRefineWidget) => {
    const { facetList, sortConfigs } = props;

    // sticky Calculation
    const [isSticky, setIsSticky] = useState(false);
    const stickyRef = useRef<HTMLDivElement>(null);
    const [offset, setTop] = useState(0);
    const { clickHandler } = useContext(WidgetControllerContext);

    const toggleSticky = useCallback(() => {
        //sticky position -> Element Height - Position of sticky from top
        const stickPosition = offset - STICKY_OFFSET;
        if (window.scrollY > stickPosition) {
            if (!isSticky) {
                setIsSticky(true);
            }
        } else {
            if (isSticky) {
                setIsSticky(false);
            }
        }
    }, [isSticky, offset]);

    //  get initial position
    useEffect(() => {
        if (!stickyRef.current) return;
        setTop((getBoundingClient(stickyRef.current)?.top || 0) + window?.scrollY);
    }, []);

    useEffect(() => {
        const handleScroll = () => {
            toggleSticky();
        };
        window.addEventListener("scroll", handleScroll);
        return () => {
            window.removeEventListener("scroll", handleScroll);
        };
    }, [toggleSticky]);

    const scrollToFilter = useCallback((id?: string) => {
        document?.getElementById(`filter-chip-${id}`)?.scrollIntoView({
            behavior: "smooth",
            block: "nearest",
            inline: "center",
        });
    }, []);

    const [sortOpen, setSortOpen] = useState(false);
    const [filterOpen, setFilterOpen] = useState(false);
    const [activeFacet, setActiveFacet] = useState<IFacetList | null>(null);

    const selectedSortValue = getSelectedSortValue(sortConfigs);
    const selectedSortLabel = getSelectedSortLabel(sortConfigs, selectedSortValue);

    const defaultSortValue = useMemo(() => getDefaultSortValue(sortConfigs), [sortConfigs]);
    const preSelectedFilters = useMemo(
        () => getAllSelectedFilter(facetList, sortConfigs),
        [facetList, sortConfigs]
    );

    // childOpenFilterList handled inside OpenFilterChips
    const filterCount = useMemo(
        () => Object.keys(preSelectedFilters)?.length,
        [preSelectedFilters]
    );

    const onClickApplyHandler = useCallback(
        (
            selectedFilters: Record<string, string[]>,
            onClose: VoidFunction,
            extraParams?: { selectedFacet?: IFacetInfo[]; facetInfo?: IFacetList }
        ) => {
            setFilterOpen(false);
            scrollToFilter();

            // Identify which filters were edited in this session
            // If activeFacet is set, only that single filter was edited
            // Otherwise, all filters in facetList were available for editing
            const editedFilterIds = activeFacet
                ? [activeFacet.id]
                : facetList.map((facet) => facet.id);

            // When a user unchecks a filter, its key is deleted from selectedFilters
            // If we spread preSelectedFilters first, unchecked filters would remain
            // So we exclude edited filters from preSelectedFilters and only keep
            // filters that weren't part of this editing session
            const otherFilters = Object.fromEntries(
                Object.entries(preSelectedFilters).filter(([key]) => !editedFilterIds.includes(key))
            );

            // Merge: otherFilters (non-edited) + selectedFilters (edited, including deletions)
            // This ensures unchecked filters are properly removed
            clickHandler?.(undefined, {
                selectedFilters: { ...otherFilters, ...selectedFilters },
                onClose,
                action: ACTIONS.HALF_CARD_CONTENT_APPLY_FILTERS,
                selectedFacet: extraParams?.selectedFacet,
                facetInfo: extraParams?.facetInfo,
            });
        },
        [clickHandler, preSelectedFilters, scrollToFilter, activeFacet, facetList]
    );

    const onClearFilters = useCallback(() => {
        clickHandler?.(undefined, {
            selectedFilters: {},
            action: ACTIONS.CLEAR_FILTERS,
            selectedFacet: [],
            facetInfo: {},
        });
    }, [clickHandler]);

    const onSortClick = useCallback(() => {
        setSortOpen(!sortOpen);
    }, [sortOpen]);

    const onOpenFilterClick = useCallback(
        (id: string, parentId: string, isSelected: boolean | undefined) => {
            if (!id || !parentId) return;

            if (isSelected) {
                // eslint-disable-next-line @typescript-eslint/naming-convention
                const _preSelectedFilters = preSelectedFilters[parentId]?.filter(
                    (_id) => _id !== id
                );
                if (_preSelectedFilters.length === 0) {
                    delete preSelectedFilters[parentId];
                } else {
                    preSelectedFilters[parentId] = _preSelectedFilters;
                }

                {
                    const parentFacet = facetList.find((f) => f.id === parentId);
                    const selectedFacet = parentFacet?.facetInfo?.find((fi) => fi.id === id);
                    clickHandler?.(undefined, {
                        selectedFilters: { ...preSelectedFilters },
                        action: ACTIONS.PILL_SELECTED_FILTERS_CLICK,
                        selectedFacet: selectedFacet ? [selectedFacet] : undefined,
                        facetInfo: parentFacet,
                    });
                }
                return;
            }

            scrollToFilter(id);

            const parentFacet = facetList.find((f) => f.id === parentId);
            const selectedFacet = parentFacet?.facetInfo?.find((fi) => fi.id === id);
            clickHandler?.(undefined, {
                selectedFilters: {
                    ...preSelectedFilters,
                    [parentId]: [id],
                },
                action: ACTIONS.PILL_UNSELECTED_FILTERS_CLICK,
                selectedFacet: selectedFacet ? [selectedFacet] : undefined,
                facetInfo: parentFacet,
            });
        },
        [clickHandler, preSelectedFilters, scrollToFilter, facetList]
    );

    const onFilterClick = useCallback(() => {
        setActiveFacet(null);
        setFilterOpen(!filterOpen);
    }, [filterOpen]);

    const onParentFacetClick = useCallback((facet: IFacetList) => {
        setActiveFacet(facet);
        setFilterOpen(true);
    }, []);

    const onSortChange = useCallback(
        (e: FormEvent) => {
            scrollToFilter();
            clickHandler?.(undefined, {
                selectedFilters: {
                    ...preSelectedFilters,
                    sortAttribute: [(e.target as HTMLInputElement).value],
                },
                action: ACTIONS.SORT_CHANGE,
            });
        },
        [clickHandler, preSelectedFilters, scrollToFilter]
    );

    return (
        <RefineWidgetPlaceholder ref={stickyRef}>
            <RefineWidgetWrapper sticky={isSticky}>
                <>
                    {facetList?.length > 0 && (
                        <RefineChip>
                            <StyledButtonChip onClick={onFilterClick} isSelected={filterCount > 0}>
                                {filterCount > 0 && (
                                    <StyledButtonChipSpan>
                                        <InnerSpan>{filterCount}</InnerSpan>
                                    </StyledButtonChipSpan>
                                )}
                                <Text variant="V2.body2.neutral.medium" color="text_High_Emphasis">
                                    {TEXTS.FILTER}
                                </Text>

                                <SpriteIconWeb
                                    iconName="filter16"
                                    strokeColor={"text_Highest_Emphasis"}
                                    fillColor={"text_Highest_Emphasis"}
                                />
                            </StyledButtonChip>
                        </RefineChip>
                    )}

                    {!isEmpty(sortConfigs) && (
                        <RefineChip>
                            <DropdownChip
                                label={
                                    selectedSortValue === defaultSortValue
                                        ? TEXTS.SORT_BY
                                        : selectedSortLabel
                                }
                                isSelected={sortOpen}
                                isOpen={sortOpen}
                                closeOnOverlayClick={true}
                                onClick={onSortClick}
                                ctaText={TEXTS.APPLY}
                                dropdown={{
                                    top: 0,
                                    left: 0,
                                    ctaText: TEXTS.APPLY,
                                    radios: {
                                        name: TEXTS.SORT,
                                        value: selectedSortValue,
                                        orientation: "rtl",
                                        items: transformSortObject(sortConfigs) ?? [],
                                        onChange: onSortChange,
                                    },
                                    root: `#${COMMON_IDS.SORT_CONTAINER}`,
                                }}
                                onClose={onSortClick}
                                dropdownRoot={`#${COMMON_IDS.SORT_CONTAINER}`}
                            />
                        </RefineChip>
                    )}

                    <OpenFilterChips
                        facetList={facetList}
                        onFacetPillClick={onParentFacetClick}
                        onSelectFacetItem={onOpenFilterClick}
                        setActiveFacet={setActiveFacet}
                        isOpen={filterOpen}
                        setFilterOpen={setFilterOpen}
                    />

                    {filterOpen && (
                        <>
                            <HalfCard
                                isOpen={filterOpen}
                                onClose={onFilterClick}
                                hideCloseIcon={false}
                                hasCustomPadding
                                overflowY="hidden"
                            >
                                <HalfCardContent
                                    sortList={sortConfigs}
                                    filterList={
                                        activeFacet
                                            ? cleanFilterData([activeFacet])
                                            : cleanFilterData(facetList)
                                    }
                                    onApply={(
                                        selectedFilters: Record<string, string[]>,
                                        extraParams
                                    ) => {
                                        onClickApplyHandler(
                                            selectedFilters,
                                            onFilterClick,
                                            extraParams
                                        );
                                    }}
                                    onClearFilters={onClearFilters}
                                />
                            </HalfCard>
                        </>
                    )}
                </>
            </RefineWidgetWrapper>
        </RefineWidgetPlaceholder>
    );
};

export default memo(RefineWidget);
